import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * Card — a surface container.
 *
 * CONVENTION (surface-system apps, e.g. signage): cards are BORDERLESS. Depth
 * comes from surface *shade* alone — a card fill sits a step apart from its
 * parent (canvas → section card → nested item card) — never from a hairline or
 * a drop shadow. Both are intentionally suppressed:
 *   • border → `--border-card`, which tracks the card surface, so the 1px box
 *     stays (no layout shift) but is invisible.
 *   • shadow → stripped app-wide inside `.app-shell` (see the app `index.css`).
 *
 * Only these still draw a visible border, via their own tokens — NOT `--border`:
 *   • form controls (buttons, inputs)  → `--border-control`
 *   • separators / dividers            → `--border` (border-b / border-t / divide-y)
 * Everything a user would call "a card" (section panels, item cards, the branch
 * selector, stat tiles) must read as borderless.
 *
 * Ad-hoc card `<div>`s that don't use this component MUST follow suit: use
 * `bg-card` (or `bg-[hsl(var(--card-inner))]` for a nested item card) with NO
 * `border border-border`. If a resting→hover outline is wanted, base it on
 * `border-[hsl(var(--border-card,var(--border)))]` (invisible at rest) plus
 * `hover:border-primary/30`. Apps without the surface tokens fall back to
 * `--border`, keeping their original hairline.
 */
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border border-[hsl(var(--border-card,var(--border)))] bg-card text-card-foreground",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
